/**
 * CetusEd Community Portal — File Upload for School
 *
 * COMPLIANCE: Parents may upload documents to support their child's
 *   evaluation or IEP process (IDEA §300.304 — evaluation procedures,
 *   parent-provided data). Uploaded files become part of the student's
 *   education record under FERPA §1232g.
 *
 *   ALLOWED FILE TYPES: PDF, DOCX, JPG, PNG. Max 20 MB per file.
 *   Files are scanned for malware server-side. PII is encrypted at rest.
 *
 * MIGRATION TARGET: The monolith S16 document storage handles file
 *   attachments within the staff client. This provides the parent upload path.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useUploadFile } from '../../api/hooks';
import { Button } from '../../ui/components/Button';

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
];

const MAX_SIZE_BYTES = 20 * 1024 * 1024;

export const FileUpload: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const uploadFile = useUploadFile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedStudentId, setSelectedStudentId] = useState(
    auth.user?.linkedStudents[0]?.id || '',
  );
  const [category, setCategory] = useState('evaluation');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    setFileError(null);

    if (!selected) {
      setFile(null);
      return;
    }

    if (!ALLOWED_TYPES.includes(selected.type)) {
      setFileError('File type not supported. Please upload PDF, DOCX, JPG, or PNG files.');
      setFile(null);
      return;
    }

    if (selected.size > MAX_SIZE_BYTES) {
      setFileError('File exceeds the 20 MB size limit.');
      setFile(null);
      return;
    }

    setFile(selected);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedStudentId || !file) return;

      try {
        await uploadFile.mutateAsync({ studentId: selectedStudentId, file, category });
        navigate('/files', { replace: true });
      } catch {
        setFileError('Upload failed. Please try again.');
      }
    },
    [selectedStudentId, file, category, uploadFile, navigate],
  );

  if (!auth.user) return null;

  return (
    <div className="file-upload-page">
      <nav className="breadcrumb">
        <Link to="/files">Files</Link> / Upload
      </nav>

      <h1>Upload Document</h1>

      <form onSubmit={handleSubmit} className="file-upload-form">
        <div className="form-field">
          <label htmlFor="fu-student">For Student</label>
          <select
            id="fu-student"
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
            required
          >
            <option value="">Select a student</option>
            {auth.user.linkedStudents.map((student) => (
              <option key={student.id} value={student.id}>
                {student.name} — {student.school}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="fu-category">Document Category</label>
          <select
            id="fu-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="evaluation">Evaluation Support</option>
            <option value="medical">Medical Records</option>
            <option value="iep">IEP Support</option>
            <option value="504">504 Support</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="fu-file">Select File</label>
          <input
            id="fu-file"
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.docx,.jpg,.jpeg,.png"
            required
          />
          {file && (
            <p className="file-info">
              {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)
            </p>
          )}
          {fileError && <p className="field-error" role="alert">{fileError}</p>}
          <p className="field-hint">
            Accepted formats: PDF, DOCX, JPG, PNG. Max size: 20 MB.
          </p>
        </div>

        <div className="form-actions">
          <Button type="submit" disabled={!file || uploadFile.isPending} isLoading={uploadFile.isPending}>
            Upload Document
          </Button>
          <Link to="/files">
            <Button variant="ghost">Cancel</Button>
          </Link>
        </div>
      </form>
    </div>
  );
};
